import DateRangeInput from 'components/common/Form/DateRange'
import { DATE_FORMATS } from 'constants/dateTime'
import { MAXIMUM_POSSIBLE_DATE } from 'constants/index'
import {
  TODAY,
  YESTERDAY,
  LAST_WEEK,
  TOMORROW,
  NEXT_WEEK,
  FILTER_TYPE_DATE,
  FILTER_TYPE_DATE_RANGE_FROM,
  FILTER_TYPE_DATE_RANGE_TO,
} from 'constants/managementUI'
import { startOfDay, addDays, endOfDay } from 'date-fns'
import { getDateFormat, getRange } from 'helpers/dates'
import { Map } from 'immutable'
import moment from 'moment'
import React, { useState } from 'react'
import { WrappedComponentProps, injectIntl } from 'react-intl'
import { ReactComponent as DatesIcon } from 'assets/img/calendar-icon.svg'
import { ReactComponent as Vector } from 'assets/img/select-vector.svg'
import { connect } from 'react-redux'
import { Profile } from 'types/common'
import { StyledDateRange } from './DateRangeSelector.styles'
import { DateRangeSelectorTypes } from './DateRangeSelector.types'
import { DateRangeInputType } from '../../FilterBar/FilterBar.types'
import messages from '../../translate'

export const DateRangeSelector = ({
  intl: { formatMessage },
  profile,
  applyFilter,
  appliedDate,
  appliedDateRangeFrom,
  appliedDateRangeTo,
}: WrappedComponentProps & DateRangeSelectorTypes) => {
  const {
    licensee: { usaDateFormat },
  } = profile.toJS()

  const [isOpen, setIsOpen] = useState(false)

  const datePickerStaticDates = {
    startOfLastWeek: startOfDay(addDays(new Date(), -7)),
    endOfLastWeek: endOfDay(addDays(new Date(), -1)),
    startOfYesterday: startOfDay(addDays(new Date(), -1)),
    endOfYesterday: endOfDay(addDays(new Date(), -1)),
    startOfToday: startOfDay(new Date()),
    endOfToday: endOfDay(new Date()),
    startOfTomorrow: startOfDay(addDays(new Date(), 1)),
    endOfTomorrow: endOfDay(addDays(new Date(), 1)),
    startOfNextWeek: startOfDay(addDays(new Date(), 1)),
    endOfNextWeek: endOfDay(addDays(new Date(), 7)),
  }
  const defaultStaticRanges = [
    getRange(
      formatMessage(messages.lastWeek),
      datePickerStaticDates.startOfLastWeek,
      datePickerStaticDates.endOfLastWeek,
    ),
    getRange(
      formatMessage(messages.yesterday),
      datePickerStaticDates.startOfYesterday,
      datePickerStaticDates.endOfYesterday,
    ),
    getRange(
      formatMessage(messages.today),
      datePickerStaticDates.startOfToday,
      datePickerStaticDates.endOfToday,
    ),
    getRange(
      formatMessage(messages.tomorrow),
      datePickerStaticDates.startOfTomorrow,
      datePickerStaticDates.endOfTomorrow,
    ),
    getRange(
      formatMessage(messages.nextWeek),
      datePickerStaticDates.startOfNextWeek,
      datePickerStaticDates.endOfNextWeek,
    ),
  ]

  const findDateFieldMatch = (data: DateRangeInputType) =>
    defaultStaticRanges.find(
      ({ startDate, endDate }) =>
        moment(startDate).format(DATE_FORMATS.DEFAULT) ===
          moment(data.startDate).format(DATE_FORMATS.DEFAULT) &&
        moment(endDate).format(DATE_FORMATS.DEFAULT) ===
          moment(data.endDate).format(DATE_FORMATS.DEFAULT),
    )

  const onApplyDatesMenu = (data: DateRangeInputType) => {
    const match = findDateFieldMatch(data)
    const formattedDateFrom = moment(data.startDate).format('YYYY-MM-DD')
    const formattedDateTo = moment(data.endDate).format('YYYY-MM-DD')

    if (
      (match &&
        appliedDate &&
        match.label === formatMessage({ id: appliedDate })) ||
      (formattedDateFrom === appliedDateRangeFrom &&
        formattedDateTo === appliedDateRangeTo)
    ) {
      return
    }

    if (match) {
      let dateFieldValue

      switch (match.label) {
        case formatMessage(messages.today):
          dateFieldValue = TODAY
          break
        case formatMessage(messages.yesterday):
          dateFieldValue = YESTERDAY
          break
        case formatMessage(messages.lastWeek):
          dateFieldValue = LAST_WEEK
          break
        case formatMessage(messages.tomorrow):
          dateFieldValue = TOMORROW
          break
        case formatMessage(messages.nextWeek):
          dateFieldValue = NEXT_WEEK
          break
        default:
      }

      applyFilter({
        [FILTER_TYPE_DATE]: dateFieldValue,
        [FILTER_TYPE_DATE_RANGE_FROM]: null,
        [FILTER_TYPE_DATE_RANGE_TO]: null,
      })
    } else {
      applyFilter({
        [FILTER_TYPE_DATE]: null,
        [FILTER_TYPE_DATE_RANGE_FROM]: formattedDateFrom,
        [FILTER_TYPE_DATE_RANGE_TO]: formattedDateTo,
      })
    }
  }

  const getDateRange = () => {
    const dateToday = [
      {
        key: 'selection',
        startDate: datePickerStaticDates.startOfToday,
        endDate: datePickerStaticDates.endOfToday,
      },
    ]
    const dateYesterday = [
      {
        key: 'selection',
        startDate: datePickerStaticDates.startOfYesterday,
        endDate: datePickerStaticDates.endOfYesterday,
      },
    ]
    const dateTomorrow = [
      {
        key: 'selection',
        startDate: datePickerStaticDates.startOfTomorrow,
        endDate: datePickerStaticDates.endOfTomorrow,
      },
    ]
    const dateLastWeek = [
      {
        key: 'selection',
        startDate: datePickerStaticDates.startOfLastWeek,
        endDate: datePickerStaticDates.endOfLastWeek,
      },
    ]
    const dateNextWeek = [
      {
        key: 'selection',
        startDate: datePickerStaticDates.startOfNextWeek,
        endDate: datePickerStaticDates.endOfNextWeek,
      },
    ]

    if (!appliedDate && !appliedDateRangeFrom && !appliedDateRangeTo) {
      return dateToday
    }

    if (appliedDate) {
      switch (appliedDate) {
        case TODAY:
          return dateToday
        case YESTERDAY:
          return dateYesterday
        case TOMORROW:
          return dateTomorrow
        case LAST_WEEK:
          return dateLastWeek
        case NEXT_WEEK:
          return dateNextWeek
        default:
      }
    } else if (appliedDateRangeFrom && appliedDateRangeTo) {
      return [
        {
          key: 'selection',
          startDate: new Date(appliedDateRangeFrom),
          endDate: new Date(appliedDateRangeTo),
        },
      ]
    }
  }

  return (
    <StyledDateRange align="center" isOpen={isOpen}>
      <DatesIcon />
      <DateRangeInput
        // @ts-expect-error 'error is thrown because of the JS DateRangeInput component'
        dateDisplayFormat={getDateFormat(usaDateFormat)}
        dateRangeProps={{
          maxDate: new Date(MAXIMUM_POSSIBLE_DATE),
          staticRanges: defaultStaticRanges,
        }}
        onClose={onApplyDatesMenu}
        range={getDateRange()}
        label={formatMessage(messages.today)}
        newEctoStyles
        hideAdornment
        setIsOpen={setIsOpen}
      />
      <Vector />
    </StyledDateRange>
  )
}

export default connect(
  (state: { getIn: (path: Array<string>) => Map<string, Profile> }) => ({
    profile: state.getIn(['profile', 'profile']) as Map<string, Profile>,
  }),
)(injectIntl(DateRangeSelector))
